import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["amount", "aria-describedby", "aria-labelledby", "autosize", "changed", "className", "counter", "fwdRef", "hasFocus", "invalid", "maxLength", "maxRows", "readonly", "required", "rows", "size", "statusMessage", "wrapperProps"];
import React, { useRef, useContext, useState } from 'react';
import classNames from 'classnames';
import AutosizeTextarea from 'react-textarea-autosize';
import { v4 as uuidv4 } from 'uuid';
import { useFocus, FormContext } from '@op/opds.opux';
import { buildVersionedClassNames } from '@op/opds.opux-utilities';
import { FormRowContext } from '../form-row/form-row';
import { useLocale } from '../common/locale';
import messages from './_messages';
import { Counter, CounterWrapper } from './_common';
const mergeIds = classNames;
export default function TextAreaAutosize(props) {
  var _messages$lc$maxLengt, _messages$lc;
  const {
      amount,
      'aria-describedby': prevDesc,
      'aria-labelledby': prevLabels,
      // Discarded from props because react-textarea-autosize doesn't have `autosize` prop and "autosizing" is always on
      changed = false,
      className,
      counter = false,
      fwdRef,
      hasFocus: propFocus,
      invalid = false,
      maxLength,
      maxRows,
      readonly: readonlyProp = false,
      required: requiredProp,
      rows = 1,
      size: propSize,
      statusMessage,
      wrapperProps
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const lc = useLocale();
  const internalRef = useRef(null);
  const inputRef = fwdRef && typeof fwdRef !== 'function' ? fwdRef : internalRef;
  const hasFocus = useFocus(inputRef, propFocus);
  const {
    spacing
  } = useContext(FormContext);
  const {
    disabled,
    id,
    labelId,
    required: requiredContext
  } = useContext(FormRowContext);
  const size = propSize || spacing;
  const required = requiredProp != null ? requiredProp : requiredContext;
  const [maxLengthId] = useState(() => "ds-ti-ml-" + uuidv4());
  const inputProps = _extends({
    'aria-describedby': mergeIds(prevDesc, {
      [maxLengthId]: maxLength
    }) || undefined,
    'aria-invalid': invalid,
    'aria-labelledby': mergeIds(prevLabels, _extends({}, labelId !== undefined && {
      [labelId]: !(prevLabels != null && prevLabels.includes(labelId)) && prevLabels
    })) || undefined,
    'aria-required': required || undefined,
    disabled,
    id,
    maxLength,
    readOnly: readonlyProp,
    required
  }, rest);
  const cn = buildVersionedClassNames('ds-text-input', className || null, {
    /**
     * Due to buildVersionedClassNames API, multiple "versioned" class names
     * can't be provided as strings. Due to this, as the 'ds-input-behavior'
     * class is always needed, we're setting this to true.
     */
    'ds-input-behavior': true,
    'ds-text-input--amount': !!amount,
    'ds-input-behavior--changed': changed,
    'ds-input-behavior--disabled': inputProps.disabled,
    'ds-input-behavior--focus': hasFocus,
    'ds-input-behavior--invalid': invalid,
    'ds-input-behavior--readonly': readonlyProp,
    ["ds-input-behavior--" + size]: size !== 'default'
  });
  const maxLengthEl = maxLength ? /*#__PURE__*/React.createElement("span", {
    id: maxLengthId,
    style: {
      display: 'none'
    }
  }, (_messages$lc$maxLengt = (_messages$lc = messages[lc]).maxLengthLabel) == null ? void 0 : _messages$lc$maxLengt.call(_messages$lc, {
    maxLength
  })) : null;
  const message = maxLength ? statusMessage : undefined;
  return /*#__PURE__*/React.createElement(React.Fragment, null, counter ? /*#__PURE__*/React.createElement(CounterWrapper, {
    className: cn,
    size: size,
    wrapperProps: wrapperProps
  }, /*#__PURE__*/React.createElement(AutosizeTextarea, _extends({}, inputProps, {
    readOnly: readonlyProp,
    className: classNames(!counter && cn),
    maxRows: maxRows,
    minRows: rows || 1,
    ref: inputRef,
    required: required
  })), /*#__PURE__*/React.createElement(Counter, {
    counter: counter,
    inputRef: inputRef,
    maxLength: maxLength,
    size: size,
    statusMessage: message,
    valueLength: String(inputProps.value).length
  })) : /*#__PURE__*/React.createElement(AutosizeTextarea, _extends({}, inputProps, {
    readOnly: readonlyProp,
    className: classNames(!counter && cn),
    maxRows: maxRows,
    minRows: rows || 1,
    ref: inputRef,
    required: required
  })), maxLengthEl);
}